/**
 * Utilities for reading data passed from Django templates.
 *
 * These functions read from JSON script blocks and data attributes,
 * providing a clean bridge between server-rendered data and JS modules.
 *
 * @module utils/template-data
 */

/**
 * Get configuration object for a component.
 * Reads from: <script type="application/json" data-config-for="component-id">
 *
 * @param {string} componentId - The component identifier
 * @returns {Object} Configuration object, or empty object if not found
 *
 * @example
 * // In template:
 * // {% js_config "my-widget" foo="bar" count=42 %}
 *
 * // In JavaScript:
 * const config = getConfig('my-widget');
 * // config = { foo: "bar", count: 42 }
 */
export function getConfig(componentId) {
  const script = document.querySelector(
    `script[type="application/json"][data-config-for="${componentId}"]`
  );

  if (!script) {
    console.warn(`[template-data] No config found for component: ${componentId}`);
    return {};
  }

  try {
    return JSON.parse(script.textContent);
  } catch (e) {
    console.error(`[template-data] Failed to parse config for ${componentId}:`, e);
    return {};
  }
}

/**
 * Get page configuration.
 * Reads from: <script type="application/json" data-page-config="page-id">
 *
 * @param {string} pageId - The page identifier
 * @returns {Object} Page configuration object, or empty object if not found
 */
export function getPageConfig(pageId) {
  const script = document.querySelector(
    `script[type="application/json"][data-page-config="${pageId}"]`
  );

  if (!script) {
    console.warn(`[template-data] No page config found for: ${pageId}`);
    return {};
  }

  try {
    return JSON.parse(script.textContent);
  } catch (e) {
    console.error(`[template-data] Failed to parse page config for ${pageId}:`, e);
    return {};
  }
}

/**
 * Get JSON data by ID.
 * Reads from: <script type="application/json" data-json-id="data-id">
 *
 * @param {string} dataId - The data identifier
 * @returns {*} Parsed JSON data, or null if not found
 *
 * @example
 * // In template:
 * // {% js_json "chart-data" chart_items %}
 *
 * // In JavaScript:
 * const chartData = getData('chart-data');
 */
export function getData(dataId) {
  const script = document.querySelector(
    `script[type="application/json"][data-json-id="${dataId}"]`
  );

  if (!script) {
    console.warn(`[template-data] No data found with id: ${dataId}`);
    return null;
  }

  try {
    return JSON.parse(script.textContent);
  } catch (e) {
    console.error(`[template-data] Failed to parse data ${dataId}:`, e);
    return null;
  }
}

/**
 * Get URL configuration.
 * Reads from: <script type="application/json" data-urls>
 *
 * @returns {Object} URL mapping object
 *
 * @example
 * // In template:
 * // {% url 'api_campaigns' as api_url %}
 * // {% js_urls campaigns_api=api_url %}
 *
 * // In JavaScript:
 * const urls = getUrls();
 * // urls = { campaigns_api: "/api/campaigns/" }
 */
export function getUrls() {
  const script = document.querySelector(
    'script[type="application/json"][data-urls]'
  );

  if (!script) {
    return {};
  }

  try {
    return JSON.parse(script.textContent);
  } catch (e) {
    console.error('[template-data] Failed to parse URL config:', e);
    return {};
  }
}

/**
 * Get a specific URL from the URL configuration.
 *
 * @param {string} name - URL name
 * @returns {string|null} URL or null if not found
 *
 * @example
 * const apiUrl = getUrl('campaigns_api');
 */
export function getUrl(name) {
  const urls = getUrls();
  return urls[name] || null;
}

/**
 * Get CSRF token from cookie or meta tag.
 *
 * @returns {string|null} CSRF token
 */
export function getCsrfToken() {
  // Try meta tag first (preferred for CSP compliance)
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    return metaTag.getAttribute('content');
  }

  // Fall back to cookie
  const cookieValue = document.cookie
    .split('; ')
    .find(row => row.startsWith('csrftoken='))
    ?.split('=')[1];

  return cookieValue || null;
}

/**
 * Get data attributes from an element as an object.
 * Converts data-foo-bar to fooBar (camelCase).
 *
 * @param {HTMLElement|string} element - Element or selector
 * @returns {Object} Data attributes as object
 *
 * @example
 * // <div id="widget" data-campaign-id="42" data-status="active">
 * const attrs = getDataAttributes('#widget');
 * // attrs = { campaignId: "42", status: "active" }
 */
export function getDataAttributes(element) {
  const el = typeof element === 'string'
    ? document.querySelector(element)
    : element;

  if (!el) {
    return {};
  }

  return { ...el.dataset };
}

/**
 * Parse a data attribute value, converting strings to appropriate types.
 *
 * @param {string} value - The string value to parse
 * @returns {*} Parsed value (number, boolean, object, or original string)
 */
export function parseDataValue(value) {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value === 'null') return null;
  if (value === 'undefined') return undefined;

  // Try to parse as number
  const num = Number(value);
  if (!isNaN(num) && value.trim() !== '') return num;

  // Try to parse as JSON
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

/**
 * Get and parse data attributes from an element.
 *
 * @param {HTMLElement|string} element - Element or selector
 * @returns {Object} Parsed data attributes
 */
export function getParsedDataAttributes(element) {
  const attrs = getDataAttributes(element);
  const parsed = {};

  for (const [key, value] of Object.entries(attrs)) {
    parsed[key] = parseDataValue(value);
  }

  return parsed;
}

/**
 * Initialize a component with its configuration.
 * Combines data attributes with JSON config.
 *
 * @param {string} selector - Element selector
 * @param {Function} initFn - Initialization function(element, config)
 * @param {Object} options - Options for initialization
 * @param {string} options.configId - Override config ID (defaults to element ID)
 * @param {boolean} options.parseDataAttrs - Parse data attributes to correct types
 *
 * @example
 * initComponent('[data-component="chart"]', (el, config) => {
 *   new Chart(el, config);
 * });
 */
export function initComponent(selector, initFn, options = {}) {
  const elements = document.querySelectorAll(selector);

  elements.forEach(el => {
    const dataAttrs = options.parseDataAttrs
      ? getParsedDataAttributes(el)
      : getDataAttributes(el);

    const configId = options.configId || dataAttrs.configId || el.id;
    const jsonConfig = configId ? getConfig(configId) : {};

    // Merge configs: JSON config first, then data attributes (data attrs override)
    const mergedConfig = { ...jsonConfig, ...dataAttrs };

    try {
      initFn(el, mergedConfig);
    } catch (e) {
      console.error(`[template-data] Failed to initialize ${selector}:`, e);
    }
  });
}

/**
 * Wait for DOM ready, then initialize component.
 *
 * @param {string} selector - Element selector
 * @param {Function} initFn - Initialization function
 * @param {Object} options - Options for initialization
 */
export function initOnReady(selector, initFn, options = {}) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initComponent(selector, initFn, options);
    });
  } else {
    initComponent(selector, initFn, options);
  }
}

/**
 * Create a configured fetch function with CSRF token.
 *
 * @param {Object} defaultOptions - Default fetch options
 * @returns {Function} Configured fetch function
 */
export function createFetch(defaultOptions = {}) {
  return async function(url, options = {}) {
    const csrfToken = getCsrfToken();

    const mergedOptions = {
      credentials: 'same-origin',
      ...defaultOptions,
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken && { 'X-CSRFToken': csrfToken }),
        ...defaultOptions.headers,
        ...options.headers,
      },
    };

    return fetch(url, mergedOptions);
  };
}

/**
 * Pre-configured fetch with CSRF token.
 */
export const fetchWithCSRF = createFetch();

// Export default object for convenience
export default {
  getConfig,
  getPageConfig,
  getData,
  getUrls,
  getUrl,
  getCsrfToken,
  getDataAttributes,
  getParsedDataAttributes,
  parseDataValue,
  initComponent,
  initOnReady,
  createFetch,
  fetchWithCSRF,
};
